import { Inject, Injectable } from '@angular/core';
import {
  BootstrapperBase,
  LogService,
  TOKENS as COMMON_TOKENS,
} from '@trustedshops/tswp-core-common';
import {
  ProductFeatureService,
  TOKENS as MASTERDATA_TOKENS,
} from '@trustedshops/tswp-core-masterdata';
import {
  IdentityService,
  Identity,
  TOKENS as AUTH_TOKENS,
} from '@trustedshops/tswp-core-authorization';
import { toPromise } from '@trustedshops/tswp-core-common-eventing-rxjs';
import { ENVIRONMENT, Environment } from '../models/environment.type';

/**
 * Bootstrapper that initializes ProductFeatureService once accountId is available.
 * accountId comes from the user's identity token (https://etrusted.com.accountRef)
 * via AccountService.accountId$.
 */
@Injectable()
export class ProductFeatureBootstrapper implements BootstrapperBase {
  private static readonly TYPE = 'ProductFeatureBootstrapper';

  public constructor(
    @Inject(ENVIRONMENT) private readonly _environment: Environment,
    @Inject(MASTERDATA_TOKENS.ProductFeatureService)
    private readonly _productFeatureService: ProductFeatureService,
    @Inject(COMMON_TOKENS.LogService) private readonly _logService: LogService,
    @Inject(AUTH_TOKENS.IdentityService)
    private readonly _identityService: IdentityService
  ) {}

  public async initialize(): Promise<void> {
    const identity = await toPromise<Identity<any>>(
      this._identityService.identity
    );
    if (!identity) {
      throw new Error('Identity not available');
    }

    let accountId: string | undefined;
    if (identity.profile?.keycloak?.token?.payload) {
      const payload = await toPromise<{
        'https://etrusted.com'?: { accountRef?: string };
      }>(identity.profile.keycloak.token.payload);
      accountId = payload?.['https://etrusted.com']?.accountRef;
    }

    if (!accountId) {
      throw new Error('Account ID not available from identity token');
    }
    this._logService.trace(
      ProductFeatureBootstrapper.TYPE,
      'Initializing ProductFeatureService...'
    );
    await this._productFeatureService.initialize({
      basePath: this._environment.apis.productFeature,
      accountId,
      // V2 companies API lives under the businessHubB2B base; the service appends
      // `/v2/companies/{accountId}`. TODO(CC-1317): confirm the exact V2 base per environment.
      companiesBasePath: this._environment.apis.businessHubB2B,
    });
    this._logService.trace(
      ProductFeatureBootstrapper.TYPE,
      'ProductFeatureService initialized successfully'
    );
  }
}
